import * as uuid from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getNodeInstanceData} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
} from 'processes/modals/router-config/RouterConfigConstants';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';

import {InaccessibleInfoGraphQL} from '../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ProcessFlowchartGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartGraphQL';
import {ProcessRouterNodeDetailsGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessRouterNodeDetailsGraphQL';
import {ProcessTaskNodeDetailsGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskNodeDetailsGraphQL';
import {
  OUTPUT_NODE_CLIENT_ID,
  ProcessFlowchartInteractiveEdge,
  ProcessFlowchartInteractiveNode,
  ROOT_NODE_CLIENT_ID,
} from '../ProcessFlowchartVizConstants';
import {isEndNodeDetailsGQL, isRouterNodeDetailsGQL, isTaskNodeDetailsGQL} from './getNodeDetailsTypeForGQL';

type ProcessRouterFunctionGraphQL = ProcessRouterNodeDetailsGraphQL['routerFunctions'][number];

type BenchlingExpressionGql = ExtractByTypename<
  ProcessRouterFunctionGraphQL['expression'],
  'BenchlingExpressionNode'
>;

type ProcessFlowchartNodeGraphQL = ProcessFlowchartGraphQL['nodeConfigs'][number];

type EdgeIdsToEdgesMap = Record<string, Array<ProcessFlowchartInteractiveEdge>>;

interface TaskSchemaForNode {
  id: string;
  name: string;
}

interface TaskSchemaForNodeWithTypename extends TaskSchemaForNode {
  __typename: string;
}

export function makeRootNode(
  name: string,
  taskSchema: TaskSchemaForNode,
  clientId?: string
): ProcessFlowchartInteractiveNode {
  return {
    clientId: clientId ?? ROOT_NODE_CLIENT_ID,
    type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    taskSchemaId: taskSchema.id,
    isInaccessible: false,
    name,
    instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS, name),
    locked: false,
    layout: {position: {x: 0, y: 0}},
  };
}

export function makeOutputNode(
  taskSchema: TaskSchemaForNode,
  clientId?: string
): ProcessFlowchartInteractiveNode {
  const name = 'Outputs';
  return {
    clientId: clientId ?? OUTPUT_NODE_CLIENT_ID,
    type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    taskSchemaId: taskSchema.id,
    isInaccessible: false,
    name,
    instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS, name),
    locked: false,
    layout: {position: {x: 0, y: 0}},
  };
}

function makeIntermediateTaskNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  clientId?: string
): ProcessFlowchartInteractiveNode {
  assert(isTaskNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected task node to have task node details');
  const taskNodeDetails = nodeGQL.nodeDetails as ProcessTaskNodeDetailsGraphQL;
  const taskSchema = taskNodeDetails.schema as TaskSchemaForNodeWithTypename | InaccessibleInfoGraphQL;
  const name = isInaccessible(taskSchema) ? NO_ACCESS_TEXT : nodeGQL.name;
  return {
    clientId: clientId ?? uuid.v4(),
    type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
    taskSchemaId: getAccessibleOrInaccessibleId(taskSchema),
    name,
    instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS, name),
    locked: false,
    isInaccessible: isInaccessible(taskSchema),
    layout: {position: {x: 0, y: 0}},
  };
}

function makeEndNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  clientId?: string
): ProcessFlowchartInteractiveNode {
  assert(isEndNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected end node to have end node details');
  return {
    clientId: clientId ?? uuid.v4(),
    type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
    instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.END_NODE_DETAILS, nodeGQL.name),
    name: nodeGQL.name,
    locked: false,
    isInaccessible: false,
    layout: {position: {x: 0, y: 0}},
  };
}

type RouterFunctionWithAccessibleExpression = ProcessRouterFunctionGraphQL & {
  expression: BenchlingExpressionGql;
};

function makeRouterFunctionSpec(
  routerFunction: ProcessRouterFunctionGraphQL,
  edgeIdsToEdges: EdgeIdsToEdgesMap
): CreateOrUpdateRouterFunctionSpec {
  const edgeConfigReference = edgeIdsToEdges[routerFunction.edgeConfig.id].find(
    (edge) =>
      edge.fromNodeClientId === routerFunction.edgeConfig.fromNodeConfig.id &&
      edge.toNodeClientId === routerFunction.edgeConfig.toNodeConfig.id
  );
  assert(edgeConfigReference != null, 'Expected edge config associated with router function to exist');

  return {
    clientId: routerFunction.id ?? uuid.v4(),
    name: routerFunction.name,
    edgeConfigReference,
    expression: isInaccessible(routerFunction.expression) ? null : routerFunction.expression.expressionJson,
    isDefault: routerFunction.isDefault,
    isConfigured: true,
  };
}

function makeRouterConfig(
  name: string,
  routerFunctions: Array<ProcessRouterFunctionGraphQL>,
  edgeIdsToEdges: EdgeIdsToEdgesMap,
  nodeDetailsClientId?: string
): RouterConfig {
  const functionSpecs: Array<CreateOrUpdateRouterFunctionSpec> = [];
  let defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null = null;
  for (const routerFunction of routerFunctions) {
    if (routerFunction.isDefault) {
      defaultFunctionSpec = makeRouterFunctionSpec(routerFunction, edgeIdsToEdges);
    } else {
      functionSpecs.push(makeRouterFunctionSpec(routerFunction, edgeIdsToEdges));
    }
  }
  return {
    clientId: nodeDetailsClientId ?? uuid.v4(),
    name,
    defaultFunctionSpec,
    functionSpecs,
  };
}

function areRouterFunctionsFullyAccessible(
  routerFunctions: Array<ProcessRouterFunctionGraphQL>
): routerFunctions is Array<RouterFunctionWithAccessibleExpression> {
  return routerFunctions.every((func) => !isInaccessible(func.expression));
}

function makeIntermediateRouterNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  edgeIdsToEdges: EdgeIdsToEdgesMap,
  clientId?: string
): ProcessFlowchartInteractiveNode {
  assert(isRouterNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected router node to have router node details');
  const routerNodeDetails = nodeGQL.nodeDetails as ProcessRouterNodeDetailsGraphQL;
  const name = routerNodeDetails.name;
  const routerConfig = makeRouterConfig(
    name,
    routerNodeDetails.routerFunctions,
    edgeIdsToEdges,
    routerNodeDetails.id
  );

  return {
    clientId: clientId ?? uuid.v4(),
    type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    routerConfig,
    name: nodeGQL.name,
    instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS, name),
    locked: false,
    isInaccessible: !areRouterFunctionsFullyAccessible(routerNodeDetails.routerFunctions),
    layout: {
      position: {x: 0, y: 0},
    },
  };
}

export function makeTaskOrControlNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  gqlEdgeIdsToClientEdges?: {[id: string]: Array<ProcessFlowchartInteractiveEdge>}
): ProcessFlowchartInteractiveNode {
  if (isTaskNodeDetailsGQL(nodeGQL.nodeDetails)) {
    return makeIntermediateTaskNode(nodeGQL, nodeGQL.id);
  } else if (isRouterNodeDetailsGQL(nodeGQL.nodeDetails)) {
    assert(
      gqlEdgeIdsToClientEdges != null,
      'Must have a GQL to client edge mapping to construct router nodes.'
    );
    return makeIntermediateRouterNode(nodeGQL, gqlEdgeIdsToClientEdges as EdgeIdsToEdgesMap, nodeGQL.id);
  } else if (isEndNodeDetailsGQL(nodeGQL.nodeDetails)) {
    return makeEndNode(nodeGQL, nodeGQL.id);
  }
  assert(false, 'Found unexpected node type.', {nodeGQL});
}
